import uniqBy from 'lodash.uniqby';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import ConnectionCards from '../components/pageComponents/Connections/ConnectionCards';
import BottomNavigation from '../components/reusable/BottomNav';
import Header from '../components/reusable/Header';
import Tabs from '../components/reusable/Tabs';
import BackButton from '../components/reusable/buttons/BackButton';
import Spinner from '../components/reusable/spinner/Spinner';
import { UserApi } from '../services';
import FollowApi from '../services/follow';
import { notifySuccess } from '../utils/helpers';

const Connections = () => {
    const location = useLocation();

    const listRef = useRef(null);
    const type = useSearchParams()[0].get('type');
    const locationState = location.state.data || {};

    const [selected, setSelected] = useState(type);
    const [isLoading, setIsLoading] = useState(true);
    const [followers, setFollowers] = useState(null);
    const [followersCount, setFollowersCount] = useState(
        locationState.followersCount || 0
    );
    const [following, setFollowing] = useState(null);
    const [followingCount, setFollowingCount] = useState(
        locationState.followingCount || 0
    );
    const [followersNextCursor, setFollowersNextCursor] = useState(null);
    const [followingNextCursor, setFollowingNextCursor] = useState(null);

    const userInfo = useSelector(state => state.user.data);

    const isFollowersTab = useMemo(() => selected === 'followers', [selected]);
    const isOwnConnectionsScreen = useMemo(
        () => locationState.userId === userInfo?.id,
        [locationState.userId, userInfo?.id]
    );

    const getFollowers = async cursor => {
        setIsLoading(true);

        try {
            const { data } = await UserApi.getFollowers(
                locationState.userId,
                cursor
            );

            if (cursor) {
                setFollowers(prev => uniqBy([...prev, ...data.followers]));
            } else {
                setFollowers(data.followers);
            }

            setFollowersCount(data.count);
            setFollowersNextCursor(data.pagination.nextCursor || null);
        } catch (error) {
            console.error('Error getting followers:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getFollowing = async cursor => {
        setIsLoading(true);

        try {
            const { data } = await UserApi.getFollowing(
                locationState.userId,
                cursor
            );

            if (cursor) {
                setFollowing(prev =>
                    uniqBy(
                        isOwnConnectionsScreen
                            ? [
                                  ...prev,
                                  ...data.following.map(f => ({
                                      ...f,
                                      isFollowedByUser: true
                                  }))
                              ]
                            : [...prev, ...data.following],
                        f => f.id
                    )
                );
            } else {
                setFollowing(
                    isOwnConnectionsScreen
                        ? data.following.map(f => ({
                              ...f,
                              isFollowedByUser: true
                          }))
                        : data.following
                );
            }
            setFollowingCount(data.count);
            setFollowingNextCursor(data.pagination.nextCursor || null);
        } catch (error) {
            console.error('Error getting following:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            await Promise.all([getFollowers(), getFollowing()]);
            setIsLoading(false);
        };
        fetchData();
    }, []);

    const follow = async followingUser => {
        try {
            setFollowers(prev =>
                prev.map(follower =>
                    follower.id === followingUser.id
                        ? {
                              ...follower,
                              isFollowedByUser: true
                          }
                        : follower
                )
            );
            if (following) {
                setFollowing(prev => [
                    ...prev,
                    { ...followingUser, isFollowedByUser: true }
                ]);
            }
            setFollowingCount(prev => prev + 1);

            const { data } = await FollowApi.follow(followingUser.id);

            notifySuccess(
                `You followed ${data.followedUser.firstName}${
                    data.followedUser.lastName
                        ? ` ${data.followedUser.lastName}`
                        : ''
                }`
            );
        } catch (error) {
            console.error('Error following:', error);

            setFollowers(prev =>
                prev.map(follower =>
                    follower.id === followingUser.id
                        ? {
                              ...follower,
                              isFollowedByUser: false
                          }
                        : follower
                )
            );
            if (following) {
                setFollowing(prev =>
                    prev.filter(follower => follower.id !== followingUser.id)
                );
            }
            setFollowingCount(prev => Math.max(prev - 1, 0));
        }
    };

    const unfollow = async unfollowingUser => {
        try {
            setFollowers(prev =>
                prev.map(follower =>
                    follower.id === unfollowingUser.id
                        ? {
                              ...follower,
                              isFollowedByUser: false
                          }
                        : follower
                )
            );
            if (following) {
                setFollowing(prev =>
                    prev.filter(follower => follower.id !== unfollowingUser.id)
                );
            }
            setFollowingCount(prev => Math.max(prev - 1, 0));

            const { data } = await FollowApi.unfollow(unfollowingUser.id);
            notifySuccess(
                `You unfollowed ${data.unfollowedUser.firstName}${
                    data.unfollowedUser.lastName
                        ? ` ${data.unfollowedUser.lastName}`
                        : ''
                }`
            );
        } catch (error) {
            console.error('Error unfollowing:', error);

            setFollowers(prev =>
                prev.map(follower =>
                    follower.id === unfollowingUser.id
                        ? {
                              ...follower,
                              isFollowedByUser: true
                          }
                        : follower
                )
            );
            if (following) {
                setFollowing(prev => [...prev, unfollowingUser]);
            }
            setFollowingCount(prev => prev + 1);
        }
    };

    const handleLoadMoreConnections = useCallback(() => {
        if (!listRef.current || isLoading) return;

        if (isFollowersTab) {
            if (!followersNextCursor) return;

            getFollowers(followersNextCursor);
        } else {
            if (!followingNextCursor) return;

            getFollowing(followingNextCursor);
        }
    }, [isFollowersTab, followersNextCursor, followingNextCursor, isLoading]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.addEventListener(
                'scroll',
                handleLoadMoreConnections
            );
            return () =>
                listRef.current?.removeEventListener(
                    'scroll',
                    handleLoadMoreConnections
                );
        }
    }, [handleLoadMoreConnections]);

    return (
        <div className='page h-[100vh] text-white bg-black flex flex-col flex-1'>
            <Header bgColor='black'>
                <div className='w-full flex items-center justify-between gap-7 mt-10'>
                    <BackButton invert from='' />
                    <div className='capitalize text-[14px]'>
                        {locationState?.fullName}
                    </div>
                    <div></div>
                </div>
            </Header>
            <div className='flex flex-col flex-1 overflow-hidden'>
                <Tabs
                    isConnections={true}
                    tabs={[
                        { connectionCount: followersCount, title: 'followers' },
                        { connectionCount: followingCount, title: 'following' }
                    ]}
                    selected={selected}
                    setSelected={setSelected}
                    color='white'
                />
                <div
                    className={`font-medium mt-[20px] overflow-hidden flex flex-col flex-1 md:grid md:grid-cols-2 gap-10 ${
                        selected.includes('highlights')
                            ? 'md:grid-rows-none md:grid-cols-none'
                            : ''
                    }`}
                >
                    {isLoading &&
                    (isFollowersTab
                        ? followers === null
                        : following === null) ? (
                        <div className='flex justify-center'>
                            <Spinner />
                        </div>
                    ) : (
                        <div
                            ref={listRef}
                            className='flex flex-col flex-1 gap-2 overflow-y-auto'
                        >
                            {(
                                isFollowersTab
                                    ? followers?.length > 0
                                    : following?.length > 0
                            ) ? (
                                (isFollowersTab ? followers : following).map(
                                    connection => {
                                        return (
                                            <div key={connection.id}>
                                                <ConnectionCards
                                                    connection={connection}
                                                    follow={follow}
                                                    unfollow={unfollow}
                                                    isOwnConnectionsScreen={
                                                        isOwnConnectionsScreen
                                                    }
                                                />
                                            </div>
                                        );
                                        // Loading spinner
                                    }
                                )
                            ) : (
                                <div className='w-full text-center'>
                                    {isFollowersTab
                                        ? `No one is following ${locationState?.fullName}`
                                        : `${locationState?.fullName} is not following anyone`}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <BottomNavigation />
        </div>
    );
};

export default Connections;
